import ErrorMessage from '@components/Common/ErrorMessage';
import EmptyState from '@components/Common/EmptyState';
import { PageHero } from '@components/Common/Panel';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import MouseOutlinedIcon from '@mui/icons-material/MouseOutlined';
import OpenInBrowserOutlinedIcon from '@mui/icons-material/OpenInBrowserOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import {
    Box,
    Card,
    CardContent,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { fetchAnalyticsRequest } from '@store/features/emailAnalytics/actions';
import type { RootState } from '@store/rootReducer';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './index.scss';
import { COUNT_Y_AXIS, linearSeries } from '@components/Common/Charts/chartAxes';
import StatTile from '@components/Common/StatTile';
import ActionIconButton from '@components/Common/ActionIconButton';
import MetricTable, { type MetricColumn } from '@components/Common/MetricTable';
import type { EmailTemplateStats } from '@store/features/emailAnalytics/actions';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import UnsubscribeOutlinedIcon from '@mui/icons-material/UnsubscribeOutlined';
import BouncePanel from './BouncePanel';

const PERIOD_OPTIONS = [
  { label: '7 days',  value: 7  },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

function fmtPct(rate: number) {
  return `${(rate * 100).toFixed(1)}%`;
}

const rateTone = (rate: number, good: number, ok: number) => (rate >= good ? 'good' : rate >= ok ? 'ok' : 'low');

const BREAKDOWN_COLUMNS: MetricColumn<EmailTemplateStats>[] = [
  {
    key: 'template',
    header: 'Template',
    variant: 'name',
    render: (t) => <span className="ea-template-key">{t.templateKey}</span>,
    sortValue: (t) => t.templateKey,
  },
  { key: 'sent', header: 'Sent', render: (t) => t.sent.toLocaleString(), sortValue: (t) => t.sent },
  { key: 'opened', header: 'Opened', render: (t) => t.opened.toLocaleString(), sortValue: (t) => t.opened },
  { key: 'clicked', header: 'Clicked', render: (t) => t.clicked.toLocaleString(), sortValue: (t) => t.clicked },
  {
    key: 'openRate',
    header: 'Open rate',
    render: (t) => (
      <Chip label={fmtPct(t.openRate)} size="small" className={`ea-rate-chip ea-rate-chip--${rateTone(t.openRate, 0.3, 0.15)}`} />
    ),
    sortValue: (t) => t.openRate,
  },
  {
    key: 'clickRate',
    header: 'Click rate',
    render: (t) => (
      <Chip label={fmtPct(t.clickRate)} size="small" className={`ea-rate-chip ea-rate-chip--${rateTone(t.clickRate, 0.05, 0.02)}`} />
    ),
    sortValue: (t) => t.clickRate,
  },
];

const EmailAnalytics = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s: RootState) => s.emailAnalytics);
  const [period, setPeriod] = useState(30);
  // Bumped by Refresh so the bounce panel, which fetches on its own, refreshes with the page.
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    dispatch(fetchAnalyticsRequest(period));
  }, [dispatch, period]);

  const handleRefresh = () => {
    dispatch(fetchAnalyticsRequest(period));
    setRefreshKey((k) => k + 1);
  };

  /**
   * A response can arrive without its parts: the resolver returns a partial
   * payload on a downstream failure, and `data` is then truthy while
   * `data.summary` is not. Reading through it crashed the page — and before
   * the route-level ErrorBoundary existed, the whole console with it.
   */
  const summary = data?.summary;
  const dailyCounts = data?.dailyCounts ?? [];
  const byTemplate = data?.byTemplate ?? [];
  const hasData = !!summary;

  const kpiCards = summary
    ? [
        { label: 'Emails sent',  value: summary.totalSent.toLocaleString('en-IN'),    icon: <EmailOutlinedIcon /> },
        { label: 'Opened',       value: summary.totalOpened.toLocaleString('en-IN'),  icon: <OpenInBrowserOutlinedIcon /> },
        { label: 'Open rate',    value: fmtPct(summary.openRate),              icon: <TrendingUpOutlinedIcon /> },
        { label: 'Clicked',      value: summary.totalClicked.toLocaleString('en-IN'), icon: <MouseOutlinedIcon /> },
        { label: 'Click rate',   value: fmtPct(summary.clickRate),             icon: <BarChartIcon /> },
        // Sends SES reported back. Optional on the type: a backend without bounce tracking omits them.
        ...(typeof summary.bounced === 'number'
          ? [{ label: 'Bounced', value: summary.bounced.toLocaleString('en-IN'), icon: <UnsubscribeOutlinedIcon /> }]
          : []),
        ...(typeof summary.complained === 'number'
          ? [{ label: 'Complained', value: summary.complained.toLocaleString('en-IN'), icon: <ReportGmailerrorredOutlinedIcon /> }]
          : []),
      ]
    : [];

  return (
    /*
     * No page shell. This is rendered only as the Email tab of Analytics, which already supplies
     * the page wrapper, the width container and the side padding. Keeping its own meant two
     * max-w-containers nested inside each other, so the tab got the horizontal padding twice and
     * sat visibly narrower and further right than every other tab on the same page, plus a second
     * page background and a min-height of a full viewport inside a tab panel.
     */
    <Box className="ea-embedded">

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <PageHero
          kicker="Email performance"
          title="Delivery, opens, and click tracking across all automated email rules."
          text={<>Last {period} days · data from <code>email_send_records</code></>}
          actions={(
            <>
              <ToggleButtonGroup
                value={period}
                exclusive
                size="small"
                onChange={(_, v) => { if (v !== null) setPeriod(v); }}
                className="ea-period-toggle"
              >
                {PERIOD_OPTIONS.map((opt) => (
                  <ToggleButton key={opt.value} value={opt.value}>
                    {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {/* An icon, as Refresh is on every other dashboard tab. */}
              <ActionIconButton label="Refresh" color="primary" icon={<RefreshIcon />} onClick={handleRefresh} busy={loading} />
            </>
          )}
        />

        {loading && !hasData && <SkeletonLoader />}

        {error && (
          <Box className="ea-error-state">
            <ErrorMessage message={error} />
          </Box>
        )}

        {hasData && (
          <>
            {/* ── KPI summary grid ──────────────────────────────────────── */}
            <Box className="cc-stat-grid">
              {kpiCards.map((kpi) => (
                <StatTile key={kpi.label} label={kpi.label} value={kpi.value} icon={kpi.icon} />
              ))}
            </Box>

            {/* ── Daily trend ─────────────────────────────────────────── */}
            <Card variant="outlined" className="cc-panel cc-panel--panel">
              <CardContent className="cc-panel__content">
                <Typography variant="subtitle1" className="ea-chart-title">
                  Daily Volume: last {period} days
                </Typography>
                {dailyCounts.length > 0 ? (
                  <LineChart
                    xAxis={[{ data: dailyCounts.map((d) => d.date), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                    series={linearSeries([
                      { data: dailyCounts.map((d) => d.sent),    label: 'Sent',    color: 'var(--acc)' },
                      { data: dailyCounts.map((d) => d.opened),  label: 'Opened',  color: 'var(--teal)' },
                      { data: dailyCounts.map((d) => d.clicked), label: 'Clicked', color: 'var(--indigo)' },
                    ])}
                    yAxis={COUNT_Y_AXIS}
                    height={280}
                    margin={{ top: 24, right: 24, bottom: 48, left: 44 }}
                  />
                ) : (
                  <EmptyState compact title="No send data for this period" />
                )}
              </CardContent>
            </Card>

            <BouncePanel days={period} refreshKey={refreshKey} />

            {/* ── Funnel + Top templates ───────────────────────────────── */}
            <Box className="ea-charts-row">
              <Card variant="outlined" className="cc-panel cc-panel--panel ea-chart-half">
                <CardContent className="cc-panel__content">
                  <Typography variant="subtitle1" className="ea-chart-title">Engagement Funnel</Typography>
                  {summary.totalSent > 0 ? (
                    <PieChart
                      series={[{
                        data: [
                          { id: 0, value: summary.totalSent,    label: 'Sent',    color: 'var(--acc)' },
                          { id: 1, value: summary.totalOpened,  label: 'Opened',  color: 'var(--teal)' },
                          { id: 2, value: summary.totalClicked, label: 'Clicked', color: 'var(--indigo)' },
                        ],
                        innerRadius: 56,
                        outerRadius: 100,
                        paddingAngle: 3,
                        cornerRadius: 4,
                      }]}
                      height={240}
                      slotProps={{ legend: { direction: 'vertical', position: { vertical: 'middle', horizontal: 'end' } } }}
                    />
                  ) : (
                    <EmptyState compact title="No data for this period" />
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" className="cc-panel cc-panel--panel ea-chart-half">
                <CardContent className="cc-panel__content">
                  <Typography variant="subtitle1" className="ea-chart-title">Top Templates by Volume</Typography>
                  {byTemplate.length > 0 ? (
                    <BarChart
                      xAxis={[{ data: byTemplate.slice(0, 10).map((t) => t.templateKey), scaleType: 'band', tickLabelStyle: { fontSize: 11 } }]}
                      series={[
                        { data: byTemplate.slice(0, 10).map((t) => t.sent),    label: 'Sent',    color: 'var(--acc)' },
                        { data: byTemplate.slice(0, 10).map((t) => t.opened),  label: 'Opened',  color: 'var(--teal)' },
                        { data: byTemplate.slice(0, 10).map((t) => t.clicked), label: 'Clicked', color: 'var(--indigo)' },
                      ]}
                      yAxis={COUNT_Y_AXIS}
                      height={240}
                      margin={{ top: 16, right: 16, bottom: 72, left: 40 }}
                    />
                  ) : (
                    <EmptyState compact title="No template data for this period" />
                  )}
                </CardContent>
              </Card>
            </Box>

            {/* ── Per-template breakdown table ─────────────────────────── */}
            <Card variant="outlined" className="cc-panel cc-panel--panel">
              <CardContent className="cc-panel__content">
                <Typography variant="subtitle1" className="ea-chart-title">Per-Template Breakdown</Typography>
                {/* A comparison table (scan a rate down the column), so MetricTable: it keeps the
                    columns side by side and scrolls on a phone instead of collapsing to cards. It
                    was a hand-rolled CSS grid with fixed 72px tracks, the shape the guardrail now
                    refuses outside Common/Table and Common/MetricTable. */}
                <MetricTable<EmailTemplateStats>
                  rows={byTemplate}
                  getRowKey={(t) => t.templateKey}
                  emptyLabel="No template data for this period"
                  columns={BREAKDOWN_COLUMNS}
                />
              </CardContent>
            </Card>
          </>
        )}

    </Box>
  );
};

export default EmailAnalytics;
